import React from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { BookOpen, GraduationCap, X } from "lucide-react";
import { useTranslation } from "react-i18next";

export type CreateContentKind = "activity" | "course";

interface CreateContentChooserModalProps {
  open: boolean;
  onClose: () => void;
  onChoose: (kind: CreateContentKind) => void;
}

const CreateContentChooserModal: React.FC<CreateContentChooserModalProps> = ({
  open,
  onClose,
  onChoose,
}) => {
  const { t } = useTranslation("wizard-path");
  const theme = useTheme();

  const options: {
    kind: CreateContentKind;
    icon: React.ReactNode;
    title: string;
    description: string;
  }[] = [
    {
      kind: "activity",
      icon: <GraduationCap size={28} color={theme.palette.primary.main} />,
      title: t("step-content.chooser.activity.title"),
      description: t("step-content.chooser.activity.description"),
    },
    {
      kind: "course",
      icon: <BookOpen size={28} color={theme.palette.primary.main} />,
      title: t("step-content.chooser.course.title"),
      description: t("step-content.chooser.course.description"),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <IconButton
        onClick={onClose}
        aria-label="close"
        sx={{
          position: "absolute",
          top: 12,
          right: 12,
          color: theme.palette.text.primary,
          zIndex: 1,
        }}
      >
        <X size={20} />
      </IconButton>
      <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
        {t("step-content.chooser.title")}
      </DialogTitle>
      <DialogContent sx={{ mt: 1 }}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          {options.map((o) => (
            <Box
              key={o.kind}
              role="button"
              tabIndex={0}
              onClick={() => onChoose(o.kind)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onChoose(o.kind);
                }
              }}
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                padding: "20px 16px",
                borderRadius: "12px",
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
                cursor: "pointer",
                transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                "&:hover, &:focus-visible": {
                  borderColor: theme.palette.primary.main,
                  boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
                  outline: "none",
                },
              }}
            >
              {o.icon}
              <Typography
                sx={{
                  fontSize: "15px",
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                }}
              >
                {o.title}
              </Typography>
              <Typography
                sx={{
                  fontSize: "13px",
                  color: theme.palette.text.secondary,
                }}
              >
                {o.description}
              </Typography>
            </Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CreateContentChooserModal;
